<template>
  <div
    class="min-h-screen flex items-center justify-center bg-cover bg-center bg-gray-50"
  >
    <!-- Card -->
    <div
      class="w-[400px] bg-white rounded-3xl shadow-xl border border-gray-100 p-8 animate-fadeInUp"
    >
      <!-- Logo -->
      <div class="text-center mb-2">
        <img
          src="@/assets/img/va.png"
          alt="VA House"
          class="h-[40px] object-contain drop-shadow-sm mx-auto"
        />
        <h2 class="text-xl font-semibold text-[#1a1a1a] mt-3 tracking-tight">
          {{
            view === "login"
              ? "Welcome Back"
              : view === "forgot"
              ? "Reset Password"
              : "Create Account"
          }}
        </h2>
        <p class="text-sm mt-1">
          {{
            view === "login"
              ? "Sign in to continue"
              : view === "forgot"
              ? forgotStep === 1
                ? "We'll email you a reset code"
                : "Enter the code and your new password"
              : step === 1
              ? "Personal Information"
              : step === 2
              ? "Account Information"
              : "Verify Your Email"
          }}
        </p>
      </div>

      <!-- FORGOT PASSWORD -->
      <form
        v-if="view === 'forgot'"
        @submit.prevent="forgotStep === 1 ? sendResetOtp() : submitPasswordReset()"
        class="space-y-5 text-left"
      >
        <div v-if="forgotStep === 1">
          <label class="block text-sm mb-1 font-medium">Email</label>
          <input
            v-model="resetEmail"
            type="email"
            placeholder="you@example.com"
            class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-sm outline-none transition"
            required
          />
        </div>

        <template v-else>
          <p class="text-sm text-gray-600 text-center">
            We sent a 6-digit code to
            <span class="font-semibold text-gray-800">{{ resetEmail }}</span>
          </p>

          <div>
            <label class="block text-sm mb-1 font-medium">Reset code</label>
            <input
              v-model="resetOtpCode"
              type="text"
              inputmode="numeric"
              maxlength="6"
              placeholder="123456"
              class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-center text-lg tracking-[0.4em] font-semibold outline-none transition"
              required
            />
          </div>

          <div>
            <label class="block text-sm mb-1 font-medium">New password</label>
            <div class="relative">
              <input
                v-model="resetPassword"
                :type="showPassword ? 'text' : 'password'"
                placeholder="••••••••"
                class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-sm outline-none transition"
                required
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-3 text-gray-600"
              >
                <icon
                  :name="showPassword ? 'eye-close' : 'eye-open'"
                  class="w-5 h-5"
                />
              </button>
            </div>
            <div v-if="resetPassword" class="mt-2">
              <div class="flex gap-1">
                <div
                  v-for="i in 4"
                  :key="i"
                  class="h-1.5 flex-1 rounded-full transition-all"
                  :class="
                    i <= resetPasswordStrength.score
                      ? resetPasswordStrength.barClass
                      : 'bg-gray-200'
                  "
                ></div>
              </div>
              <p
                class="text-xs mt-1 font-medium"
                :class="resetPasswordStrength.textClass"
              >
                {{ resetPasswordStrength.label }}
              </p>
            </div>
          </div>

          <div>
            <label class="block text-sm mb-1 font-medium"
              >Confirm new password</label
            >
            <div class="relative">
              <input
                v-model="resetConfirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="••••••••"
                class="w-full border rounded-lg px-4 py-2.5 text-sm outline-none transition"
                :class="
                  resetConfirmPassword && !resetPasswordsMatch
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-gray-300 focus:border-orange-500'
                "
                required
              />
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="absolute right-3 top-3 text-gray-600"
              >
                <icon
                  :name="showConfirmPassword ? 'eye-close' : 'eye-open'"
                  class="w-5 h-5"
                />
              </button>
            </div>
            <p
              v-if="resetConfirmPassword && !resetPasswordsMatch"
              class="text-xs text-red-500 mt-1"
            >
              Passwords do not match
            </p>
          </div>

          <div class="flex items-center justify-between text-xs">
            <button
              type="button"
              @click="sendResetOtp"
              :disabled="resendCooldown > 0 || sendingResetOtp"
              class="text-orange-500 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
            >
              {{
                resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend code"
              }}
            </button>
            <span class="text-gray-500">Code expires in 5 minutes</span>
          </div>
        </template>

        <button
          type="submit"
          :disabled="
            (forgotStep === 1 && sendingResetOtp) ||
            (forgotStep === 2 &&
              (resettingPassword ||
                resetOtpCode.length !== 6 ||
                !isResetPasswordStrong ||
                !resetPasswordsMatch))
          "
          class="w-full bg-gradient-to-r from-orange-500 to-orange-400 text-white font-semibold rounded-lg py-2.5 text-sm hover:from-orange-600 hover:to-orange-500 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{
            forgotStep === 1
              ? sendingResetOtp
                ? "Sending..."
                : "Send reset code"
              : resettingPassword
              ? "Resetting..."
              : "Reset password"
          }}
        </button>

        <button
          v-if="forgotStep === 2"
          type="button"
          @click="forgotStep = 1"
          class="w-full border border-gray-300 rounded-lg py-2.5 text-sm hover:bg-gray-50 transition"
        >
          Back
        </button>
      </form>

      <!-- LOGIN FORM -->
      <form v-else-if="view === 'login'" @submit.prevent="login" class="space-y-5 text-left">
        <div>
          <label class="block text-sm mb-1 font-medium">Email</label>
          <input
            v-model="email"
            type="email"
            placeholder="you@example.com"
            class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-sm outline-none transition"
            required
          />
        </div>

        <div>
          <label class="block text-sm mb-1 font-medium">Password</label>
          <div class="relative">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="••••••••"
              class="w-full border rounded-lg px-4 py-2.5 text-sm outline-none transition"
              :class="
                loginAttemptsRemaining !== null &&
                loginAttemptsRemaining < 5 &&
                !isAccountLocked
                  ? 'border-red-400 focus:border-red-500'
                  : 'border-gray-300 focus:border-orange-500'
              "
              :disabled="isAccountLocked"
              required
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute right-3 top-3 text-gray-600"
            >
              <icon
                :name="showPassword ? 'eye-close' : 'eye-open'"
                class="w-5 h-5"
              />
            </button>
          </div>

          <!-- Attempts / lock message -->
          <p
            v-if="isAccountLocked"
            class="text-xs mt-1 text-red-600 font-medium"
          >
            Account locked. Try again in {{ formattedLockCountdown }}.
          </p>
          <p
            v-else-if="
              loginAttemptsRemaining !== null && loginAttemptsRemaining < 5
            "
            class="text-xs mt-1"
            :class="
              loginAttemptsRemaining <= 2 ? 'text-red-500' : 'text-yellow-600'
            "
          >
            Incorrect password. {{ loginAttemptsRemaining }}
            {{ loginAttemptsRemaining === 1 ? "attempt" : "attempts" }}
            remaining before your account is locked for 30 minutes.
          </p>
        </div>

        <div class="text-right">
          <button
            type="button"
            @click="openForgotPassword"
            class="text-sm text-orange-500 hover:underline"
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          :disabled="isAccountLocked || loggingIn"
          class="w-full bg-gradient-to-r from-orange-500 to-orange-400 text-white font-semibold rounded-lg py-2.5 text-sm hover:from-orange-600 hover:to-orange-500 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{
            isAccountLocked
              ? `Locked (${formattedLockCountdown})`
              : loggingIn
              ? "Signing in..."
              : "Login"
          }}
        </button>
      </form>

      <!-- REGISTER FORM WITH STEPPER -->
      <form v-else @submit.prevent="register" class="space-y-5 text-left">
        <!-- Stepper -->
        <div class="flex justify-center mt-2">
          <div
            v-for="(label, index) in steps"
            :key="index"
            class="flex items-center"
          >
            <div
              class="w-8 h-8 text-xs flex items-center justify-center rounded-full border-2"
              :class="{
                'border-orange-500 bg-orange-500 text-white': step > index,
                'border-orange-400  text-white': step === index + 1,
                'border-gray-300   ': step < index + 1,
              }"
            >
              {{ index + 1 }}
            </div>
            <span
              v-if="index < steps.length - 1"
              class="w-8 h-[2px]"
              :class="{
                'bg-orange-500': step > index + 1,
                'bg-gray-300': step <= index + 1,
              }"
            ></span>
          </div>
        </div>

        <!-- Step Container with auto height animation -->
        <transition name="fade" mode="out-in">
          <div :key="step" class="relative">
            <div
              class="space-y-4 transition-all duration-300"
              :class="{
                'min-h-[200px]': step === 1,
                'min-h-[320px]': step === 2,
                'min-h-[240px]': step === 3,
              }"
            >
              <!-- STEP 1: Personal Info -->
              <template v-if="step === 1">
                <div>
                  <label class="block text-sm mb-1 font-medium"
                    >First Name</label
                  >
                  <input
                    v-model="form.first_name"
                    type="text"
                    placeholder="Enter first name"
                    class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-sm outline-none transition"
                  />
                </div>

                <div>
                  <label class="block text-sm mb-1 font-medium">
                    Middle Name
                    <span class="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    v-model="form.middle_name"
                    type="text"
                    placeholder="Enter middle name (optional)"
                    class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-sm outline-none transition"
                  />
                </div>

                <div>
                  <label class="block text-sm mb-1 font-medium"
                    >Last Name</label
                  >
                  <input
                    v-model="form.last_name"
                    type="text"
                    placeholder="Enter last name"
                    class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-sm outline-none transition"
                  />
                </div>

                <div>
                  <label class="block text-sm mb-1 font-medium">Suffix</label>
                  <input
                    v-model="form.suffix"
                    type="text"
                    placeholder="Enter suffix"
                    class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-sm outline-none transition"
                  />
                </div>

                <button
                  type="button"
                  @click="nextStep"
                  class="w-full bg-gradient-to-r from-orange-500 to-orange-400 text-white font-semibold rounded-lg py-2.5 text-sm hover:from-orange-600 hover:to-orange-500 transition-all shadow-md hover:shadow-lg"
                >
                  Next
                </button>
              </template>

              <!-- STEP 2: Account Info -->
              <template v-else-if="step === 2">
                <div>
                  <label class="block text-sm mb-1 font-medium">Email</label>
                  <input
                    v-model="form.email"
                    type="email"
                    placeholder="you@example.com"
                    class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-sm outline-none transition"
                  />
                </div>

                <div>
                  <label class="block text-sm mb-1 font-medium">Password</label>
                  <div class="relative">
                    <input
                      v-model="form.password"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="••••••••"
                      class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-sm outline-none transition"
                      required
                    />
                    <button
                      type="button"
                      @click="showPassword = !showPassword"
                      class="absolute right-3 top-3 text-gray-600"
                    >
                      <icon
                        :name="showPassword ? 'eye-close' : 'eye-open'"
                        class="w-5 h-5"
                      />
                    </button>
                  </div>

                  <!-- Strength meter -->
                  <div v-if="form.password" class="mt-2">
                    <div class="flex gap-1">
                      <div
                        v-for="i in 4"
                        :key="i"
                        class="h-1.5 flex-1 rounded-full transition-all"
                        :class="
                          i <= passwordStrength.score
                            ? passwordStrength.barClass
                            : 'bg-gray-200'
                        "
                      ></div>
                    </div>
                    <p
                      class="text-xs mt-1 font-medium"
                      :class="passwordStrength.textClass"
                    >
                      {{ passwordStrength.label }}
                    </p>
                    <ul class="text-xs text-gray-500 mt-1 space-y-0.5">
                      <li
                        :class="passwordChecks.length ? 'text-green-600' : ''"
                      >
                        {{ passwordChecks.length ? "✓" : "•" }} At least 8
                        characters
                      </li>
                      <li
                        :class="
                          passwordChecks.upperLower ? 'text-green-600' : ''
                        "
                      >
                        {{ passwordChecks.upperLower ? "✓" : "•" }} Upper &amp;
                        lower case letters
                      </li>
                      <li
                        :class="passwordChecks.number ? 'text-green-600' : ''"
                      >
                        {{ passwordChecks.number ? "✓" : "•" }} At least one
                        number
                      </li>
                      <li
                        :class="passwordChecks.symbol ? 'text-green-600' : ''"
                      >
                        {{ passwordChecks.symbol ? "✓" : "•" }} At least one
                        symbol
                      </li>
                    </ul>
                  </div>
                </div>

                <div>
                  <label class="block text-sm mb-1 font-medium"
                    >Confirm Password</label
                  >
                  <div class="relative">
                    <input
                      v-model="confirmPassword"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      placeholder="••••••••"
                      class="w-full border rounded-lg px-4 py-2.5 text-sm outline-none transition"
                      :class="
                        confirmPassword && !passwordsMatch
                          ? 'border-red-400 focus:border-red-500'
                          : 'border-gray-300 focus:border-orange-500'
                      "
                      required
                    />
                    <button
                      type="button"
                      @click="showConfirmPassword = !showConfirmPassword"
                      class="absolute right-3 top-3 text-gray-600"
                    >
                      <icon
                        :name="showConfirmPassword ? 'eye-close' : 'eye-open'"
                        class="w-5 h-5"
                      />
                    </button>
                  </div>
                  <p
                    v-if="confirmPassword && !passwordsMatch"
                    class="text-xs text-red-500 mt-1"
                  >
                    Passwords do not match
                  </p>
                  <p
                    v-else-if="confirmPassword && passwordsMatch"
                    class="text-xs text-green-600 mt-1"
                  >
                    ✓ Passwords match
                  </p>
                </div>

                <div class="flex justify-between gap-3">
                  <button
                    type="button"
                    @click="prevStep"
                    class="w-1/2 border border-gray-300 rounded-lg py-2.5 text-sm hover:bg-gray-50 transition"
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    @click="sendOtp"
                    :disabled="!canProceedToOtp || sendingOtp"
                    class="w-1/2 bg-gradient-to-r from-orange-500 to-orange-400 text-white font-semibold rounded-lg py-2.5 text-sm hover:from-orange-600 hover:to-orange-500 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ sendingOtp ? "Sending..." : "Send OTP" }}
                  </button>
                </div>
              </template>

              <!-- STEP 3: OTP Verification -->
              <template v-else>
                <p class="text-sm text-gray-600 text-center">
                  We sent a 6-digit code to
                  <span class="font-semibold text-gray-800">{{
                    form.email
                  }}</span>
                </p>

                <div>
                  <label class="block text-sm mb-1 font-medium"
                    >Enter OTP Code</label
                  >
                  <input
                    v-model="otpCode"
                    type="text"
                    inputmode="numeric"
                    maxlength="6"
                    placeholder="123456"
                    class="w-full border border-gray-300 focus:border-orange-500 rounded-lg px-4 py-2.5 text-center text-lg tracking-[0.4em] font-semibold outline-none transition"
                  />
                </div>

                <div class="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    @click="sendOtp"
                    :disabled="resendCooldown > 0 || sendingOtp"
                    class="text-orange-500 hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                  >
                    {{
                      resendCooldown > 0
                        ? `Resend in ${resendCooldown}s`
                        : "Resend code"
                    }}
                  </button>
                  <span class="text-gray-500">Code expires in 5 minutes</span>
                </div>

                <div class="flex justify-between gap-3">
                  <button
                    type="button"
                    @click="prevStep"
                    class="w-1/2 border border-gray-300 rounded-lg py-2.5 text-sm hover:bg-gray-50 transition"
                  >
                    Back
                  </button>

                  <button
                    type="submit"
                    :disabled="otpCode.length !== 6 || submitting"
                    class="w-1/2 bg-gradient-to-r from-orange-500 to-orange-400 text-white font-semibold rounded-lg py-2.5 text-sm hover:from-orange-600 hover:to-orange-500 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ submitting ? "Verifying..." : "Verify & Register" }}
                  </button>
                </div>
              </template>
            </div>
          </div>
        </transition>
      </form>

      <!-- Footer -->
      <div class="text-center mt-6 text-sm">
        <template v-if="view === 'forgot'">
          <span>Remember your password?</span>
          <button
            @click="setView('login')"
            class="text-orange-500 font-medium hover:underline ml-1 transition-colors"
          >
            Back to login
          </button>
        </template>
        <template v-else>
          <span>
            {{ view === "login" ? "Don’t have an account?" : "Already have an account?" }}
          </span>
          <button
            @click="toggleForm"
            class="text-orange-500 font-medium hover:underline ml-1 transition-colors"
          >
            {{ view === "login" ? "Register" : "Login" }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { toast } from "vue3-toastify";
import icon from "@/assets/icon.vue";
export default {
  name: "AuthPage",
  components: { icon },
  data() {
    return {
      view: "login",
      forgotStep: 1,
      resetEmail: "",
      resetPassword: "",
      resetConfirmPassword: "",
      resetOtpCode: "",
      sendingResetOtp: false,
      resettingPassword: false,
      step: 1,
      steps: ["Personal", "Account", "Verify"],
      email: "",
      password: "",
      showPassword: false,
      loggingIn: false,
      loginAttemptsRemaining: null,
      lockedUntil: null,
      lockCountdown: 0,
      lockTimer: null,
      showConfirmPassword: false,
      confirmPassword: "",
      otpCode: "",
      sendingOtp: false,
      submitting: false,
      resendCooldown: 0,
      resendTimer: null,
      form: {
        first_name: "",
        middle_name: "",
        last_name: "",
        suffix: "",
        email: "",
        password: "",
        employeeID: null,
      },
    };
  },
  computed: {
    isAccountLocked() {
      return this.lockedUntil !== null && this.lockCountdown > 0;
    },
    formattedLockCountdown() {
      const total = Math.max(0, this.lockCountdown);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${String(s).padStart(2, "0")}`;
    },
    passwordChecks() {
      const p = this.form.password || "";
      return {
        length: p.length >= 8,
        upperLower: /[a-z]/.test(p) && /[A-Z]/.test(p),
        number: /\d/.test(p),
        symbol: /[^A-Za-z0-9]/.test(p),
      };
    },
    passwordStrength() {
      const c = this.passwordChecks;
      const score =
        (c.length ? 1 : 0) +
        (c.upperLower ? 1 : 0) +
        (c.number ? 1 : 0) +
        (c.symbol ? 1 : 0);
      const map = [
        {
          label: "Too weak",
          barClass: "bg-red-400",
          textClass: "text-red-500",
        },
        { label: "Weak", barClass: "bg-red-400", textClass: "text-red-500" },
        {
          label: "Fair",
          barClass: "bg-yellow-400",
          textClass: "text-yellow-600",
        },
        {
          label: "Good",
          barClass: "bg-blue-400",
          textClass: "text-blue-600",
        },
        {
          label: "Strong",
          barClass: "bg-green-500",
          textClass: "text-green-600",
        },
      ];
      return { score, ...map[score] };
    },
    passwordsMatch() {
      return (
        this.form.password.length > 0 &&
        this.form.password === this.confirmPassword
      );
    },
    isPasswordStrong() {
      return this.passwordStrength.score >= 3;
    },
    canProceedToOtp() {
      return !!this.form.email && this.isPasswordStrong && this.passwordsMatch;
    },
    resetPasswordChecks() {
      const p = this.resetPassword || "";
      return {
        length: p.length >= 8,
        upperLower: /[a-z]/.test(p) && /[A-Z]/.test(p),
        number: /\d/.test(p),
        symbol: /[^A-Za-z0-9]/.test(p),
      };
    },
    resetPasswordStrength() {
      const c = this.resetPasswordChecks;
      const score =
        (c.length ? 1 : 0) +
        (c.upperLower ? 1 : 0) +
        (c.number ? 1 : 0) +
        (c.symbol ? 1 : 0);
      const map = [
        {
          label: "Too weak",
          barClass: "bg-red-400",
          textClass: "text-red-500",
        },
        { label: "Weak", barClass: "bg-red-400", textClass: "text-red-500" },
        {
          label: "Fair",
          barClass: "bg-yellow-400",
          textClass: "text-yellow-600",
        },
        {
          label: "Good",
          barClass: "bg-blue-400",
          textClass: "text-blue-600",
        },
        {
          label: "Strong",
          barClass: "bg-green-500",
          textClass: "text-green-600",
        },
      ];
      return { score, ...map[score] };
    },
    resetPasswordsMatch() {
      return (
        this.resetPassword.length > 0 &&
        this.resetPassword === this.resetConfirmPassword
      );
    },
    isResetPasswordStrong() {
      return this.resetPasswordStrength.score >= 3;
    },
  },
  beforeUnmount() {
    if (this.resendTimer) clearInterval(this.resendTimer);
    if (this.lockTimer) clearInterval(this.lockTimer);
  },
  methods: {
    setView(next) {
      this.view = next;
      if (next === "login") {
        this.forgotStep = 1;
        this.resetEmail = "";
        this.resetPassword = "";
        this.resetConfirmPassword = "";
        this.resetOtpCode = "";
        this.sendingResetOtp = false;
        this.resettingPassword = false;
      }
    },
    openForgotPassword() {
      this.resetEmail = this.email || "";
      this.forgotStep = 1;
      this.resetPassword = "";
      this.resetConfirmPassword = "";
      this.resetOtpCode = "";
      this.view = "forgot";
    },
    toggleForm() {
      this.view = this.view === "login" ? "register" : "login";
      this.step = 1;
      this.email = "";
      this.password = "";
      this.loginAttemptsRemaining = null;
      this.confirmPassword = "";
      this.otpCode = "";
      this.sendingOtp = false;
      this.submitting = false;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      this.resendCooldown = 0;
      this.form = {
        first_name: "",
        middle_name: "",
        last_name: "",
        suffix: "",
        email: "",
        password: "",
        employeeID: null,
      };
    },

    startResendCooldown() {
      this.resendCooldown = 30;
      if (this.resendTimer) clearInterval(this.resendTimer);
      this.resendTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer);
          this.resendTimer = null;
        }
      }, 1000);
    },

    async sendResetOtp() {
      if (!this.resetEmail?.trim()) {
        toast.warning("Email is required.");
        return;
      }
      try {
        this.sendingResetOtp = true;
        await axios.post(
          process.env.VUE_APP_API_BASE_URL + "/auth/forgot-password",
          { email: this.resetEmail.trim() },
        );
        toast.success(
          "If an account exists for this email, a reset code has been sent.",
        );
        this.forgotStep = 2;
        this.startResendCooldown();
      } catch (error) {
        const raw = error?.response?.data?.message;
        const msg = Array.isArray(raw) ? raw.join(", ") : raw;
        toast.error(msg || "Failed to send reset code. Try again.");
      } finally {
        this.sendingResetOtp = false;
      }
    },

    async submitPasswordReset() {
      if (this.resetOtpCode.length !== 6) {
        toast.warning("Please enter the 6-digit reset code.");
        return;
      }
      if (!this.isResetPasswordStrong) {
        toast.warning("Please choose a stronger password.");
        return;
      }
      if (!this.resetPasswordsMatch) {
        toast.warning("Passwords do not match.");
        return;
      }
      try {
        this.resettingPassword = true;
        await axios.post(
          process.env.VUE_APP_API_BASE_URL + "/auth/reset-password",
          {
            email: this.resetEmail.trim(),
            code: this.resetOtpCode,
            password: this.resetPassword,
          },
        );
        toast.success("Password reset successfully. You can sign in now.");
        this.email = this.resetEmail.trim();
        this.password = "";
        this.setView("login");
      } catch (error) {
        const raw = error?.response?.data?.message;
        const msg = Array.isArray(raw) ? raw.join(", ") : raw;
        toast.error(msg || "Password reset failed. Please try again.");
      } finally {
        this.resettingPassword = false;
      }
    },

    async sendOtp() {
      if (!this.form.email) {
        toast.warning("Email is required.");
        return;
      }
      if (!this.isPasswordStrong) {
        toast.warning("Please choose a stronger password.");
        return;
      }
      if (!this.passwordsMatch) {
        toast.warning("Passwords do not match.");
        return;
      }
      try {
        this.sendingOtp = true;

        await axios.post(
          process.env.VUE_APP_API_BASE_URL + "/user/check-duplicate",
          {
            email: this.form.email,
            first_name: this.form.first_name,
            middle_name: this.form.middle_name,
            last_name: this.form.last_name,
          },
        );

        await axios.post(process.env.VUE_APP_API_BASE_URL + "/auth/send-otp", {
          email: this.form.email,
        });
        toast.success("OTP sent to your email.");
        this.step = 3;
        this.startResendCooldown();
      } catch (error) {
        console.error("Send OTP error:", error);
        const status = error?.response?.status;
        const raw = error?.response?.data?.message;
        const msg = Array.isArray(raw) ? raw.join(", ") : raw;
        if (status === 409) {
          toast.error(msg || "Account already exists.");
        } else {
          toast.error(msg || "Failed to send OTP. Try again.");
        }
      } finally {
        this.sendingOtp = false;
      }
    },

    nextStep() {
      if (!this.form.first_name || !this.form.last_name) {
        toast.warning("Please fill in your first and last name.");
        return;
      }
      this.step = 2;
    },
    prevStep() {
      if (this.step > 1) this.step -= 1;
    },
    startLockCountdown(retryAfterMs) {
      if (this.lockTimer) clearInterval(this.lockTimer);
      this.lockedUntil = Date.now() + retryAfterMs;
      this.lockCountdown = Math.ceil(retryAfterMs / 1000);
      this.lockTimer = setInterval(() => {
        const remaining = Math.ceil((this.lockedUntil - Date.now()) / 1000);
        if (remaining <= 0) {
          clearInterval(this.lockTimer);
          this.lockTimer = null;
          this.lockedUntil = null;
          this.lockCountdown = 0;
          this.loginAttemptsRemaining = null;
        } else {
          this.lockCountdown = remaining;
        }
      }, 1000);
    },

    async login() {
      if (this.isAccountLocked) {
        toast.error(
          `Account is locked. Try again in ${this.formattedLockCountdown}.`,
        );
        return;
      }
      if (!this.email || !this.password) {
        toast.warning("Please enter email and password.");
        return;
      }
      try {
        this.loggingIn = true;
        const response = await axios.post(
          process.env.VUE_APP_API_BASE_URL + "/auth/login",
          {
            email: this.email,
            password: this.password,
          },
          { withCredentials: true },
        );

        this.loginAttemptsRemaining = null;
        this.lockedUntil = null;
        this.lockCountdown = 0;

        const { role_code, status } = response.data;

        if (status !== "Active") {
          toast.info("Your account is pending for approval!");
          return;
        }

        const code = String(role_code || "")
          .trim()
          .toUpperCase();
        if (
          code === "ADMIN" ||
          code === "HR" ||
          code === "VAM" ||
          code === "PROVIDER"
        ) {
          this.$router.push("/dashboard");
        } else {
          toast.error("Unknown role. Please contact admin.");
        }
      } catch (error) {
        console.error("Login error:", error);
        const status = error?.response?.status;
        const data = error?.response?.data || {};

        if (status === 429 || data.locked) {
          const retryAfterMs =
            data.retryAfterMs ||
            (data.lockedUntil ? data.lockedUntil - Date.now() : 30 * 60 * 1000);
          this.startLockCountdown(retryAfterMs);
          toast.error(
            data.message ||
              "Your account has been locked for 30 minutes due to too many failed attempts.",
          );
          this.password = "";
        } else if (
          status === 401 &&
          typeof data.attemptsRemaining === "number"
        ) {
          this.loginAttemptsRemaining = data.attemptsRemaining;
          toast.error(
            `Incorrect password. ${data.attemptsRemaining} ${
              data.attemptsRemaining === 1 ? "attempt" : "attempts"
            } remaining.`,
          );
        } else {
          const msg =
            data.message || "Login failed. Please check your credentials.";
          toast.error(Array.isArray(msg) ? msg.join(", ") : msg);
        }
      } finally {
        this.loggingIn = false;
      }
    },
    async register() {
      if (!this.form.email || !this.form.password) {
        toast.warning("Please complete all required fields.");
        return;
      }
      if (!this.passwordsMatch) {
        toast.warning("Passwords do not match.");
        this.step = 2;
        return;
      }
      if (!this.isPasswordStrong) {
        toast.warning("Please choose a stronger password.");
        this.step = 2;
        return;
      }
      if (this.otpCode.length !== 6) {
        toast.warning("Please enter the 6-digit OTP code.");
        return;
      }

      try {
        this.submitting = true;
        await axios.post(
          process.env.VUE_APP_API_BASE_URL + "/auth/verify-otp",
          { email: this.form.email, code: this.otpCode },
        );

        await axios.post(
          process.env.VUE_APP_API_BASE_URL + "/user/register",
          this.form,
        );

        toast.success("Registration successful!");
        this.toggleForm();
      } catch (error) {
        console.error("Registration error:", error);
        const msg =
          error?.response?.data?.message ||
          "Registration failed. Please try again.";
        toast.error(Array.isArray(msg) ? msg.join(", ") : msg);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out;
}

/* Smooth step transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
